import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { ProtectedRoute } from "../components/ProtectedRoute";
import { getContractById, Contract } from "../api/dataverse";
import { dataverseScopes } from "../auth/msalConfig";


const ContractDetailContent: React.FC = () => {
  const { contractId } = useParams<{ contractId: string }>();
  const [contract, setContract] = useState<Contract | null>(null);
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState<{ name: string; webUrl: string; size: number; createdDate: string }[]>([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const { instance } = useMsal();

  useEffect(() => {
    const fetchContract = async () => {
      if (!contractId) return;
      setLoading(true);
      try {
        const tokenResponse = await instance.acquireTokenSilent({
          scopes: dataverseScopes,
          account: instance.getActiveAccount() || undefined,
        });
        const result = await getContractById(tokenResponse.accessToken, contractId);
        setContract(result);
      } catch (err) {
        console.error("Error fetching contract:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchContract();
  }, [contractId, instance]);

  useEffect(() => {
    if (!contract?.id) return;
    setDocsLoading(true);
    const apiBase = process.env.REACT_APP_API_URL || (window.location.hostname === "localhost" ? "http://localhost:7071/api" : "/api");
    fetch(`${apiBase}/contracts/${contract.id}/documents?contractStringId=${encodeURIComponent(contract.contractId)}`)
      .then(r => r.json())
      .then(data => setDocuments(data.value ?? []))
      .catch(console.error)
      .finally(() => setDocsLoading(false));
  }, [contract?.id, contract?.contractId]);

  const formatDate = (iso: string) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("es-PR", { year: "numeric", month: "long", day: "numeric" });
  };

  const formatCurrency = (n: number) =>
    n.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 });

  const daysUntilExpiry = (fecha: string) => {
    if (!fecha) return null;
    return Math.floor((new Date(fecha).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center h-64 text-gray-400">Cargando contrato...</div>
      </div>
    );
  }

  if (!contract) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-16">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Contrato no encontrado</h2>
          <p className="text-gray-500 mb-6">El contrato "{contractId}" no existe o no tiene acceso.</p>
          <Link to="/contratos" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition">
            Volver a Contratos
          </Link>
        </div>
      </div>
    );
  }

  const days = daysUntilExpiry(contract.fechaVencimiento);
  const isOverdue = days !== null && days < 0;
  const isExpiringSoon = days !== null && days >= 0 && days <= 90;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6">
        <Link to="/contratos" className="hover:text-blue-600 transition">Contratos</Link>
        <span>/</span>
        <span className="text-gray-900 font-medium">{contract.contractId}</span>
      </nav>

      {/* Header */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden mb-8">
        <div className={`px-8 py-6 ${isOverdue ? "bg-red-50" : isExpiringSoon ? "bg-amber-50" : "bg-blue-50"}`}>
          <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{contract.contractId}</h1>
              <p className="text-lg text-gray-600 mt-1">{contract.clientName}</p>
              <div className="flex items-center gap-3 mt-3">
                <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                  {contract.municipio}
                </span>
                <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                  {contract.categoria}
                </span>
                <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                  contract.activo ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
                }`}>
                  {contract.status}
                </span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-gray-900">{formatCurrency(contract.cuantia)}</div>
              <div className="text-sm text-gray-500">Cuantía del contrato</div>
            </div>
          </div>
        </div>

        {/* Expiry Banner */}
        {isOverdue && (
          <div className="px-8 py-3 bg-red-600 text-white flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <span className="font-medium">Contrato vencido hace {Math.abs(days!)} días</span>
          </div>
        )}
        {isExpiringSoon && !isOverdue && (
          <div className="px-8 py-3 bg-amber-500 text-white flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="font-medium">Contrato vence en {days} días</span>
          </div>
        )}
      </div>

      {/* Details Grid */}
      <div className="grid md:grid-cols-2 gap-8">
        {/* Contract Details */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-5 py-3 bg-gray-50 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-700">Detalles del Contrato</h3>
          </div>
          <div className="divide-y divide-gray-100">
            <div className="px-5 py-3 flex justify-between">
              <span className="text-sm text-gray-500">Número de Contrato</span>
              <span className="text-sm font-medium text-gray-900">{contract.contractId}</span>
            </div>
            <div className="px-5 py-3 flex justify-between">
              <span className="text-sm text-gray-500">Categoría</span>
              <span className="text-sm font-medium text-gray-900">{contract.categoria}</span>
            </div>
            <div className="px-5 py-3 flex justify-between">
              <span className="text-sm text-gray-500">Cuantía</span>
              <span className="text-sm font-bold text-gray-900">{formatCurrency(contract.cuantia)}</span>
            </div>
            <div className="px-5 py-3 flex justify-between">
              <span className="text-sm text-gray-500">Estado</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                contract.activo ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
              }`}>
                {contract.status}
              </span>
            </div>
          </div>
        </div>

        {/* Dates */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-5 py-3 bg-gray-50 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-700">Fechas</h3>
          </div>
          <div className="divide-y divide-gray-100">
            <div className="px-5 py-3 flex justify-between">
              <span className="text-sm text-gray-500">Fecha de Contrato</span>
              <span className="text-sm font-medium text-gray-900">{formatDate(contract.fechaContrato)}</span>
            </div>
            <div className="px-5 py-3 flex justify-between">
              <span className="text-sm text-gray-500">Fecha de Vencimiento</span>
              <span className={`text-sm font-medium ${isOverdue ? "text-red-600" : isExpiringSoon ? "text-amber-600" : "text-gray-900"}`}>
                {formatDate(contract.fechaVencimiento)}
              </span>
            </div>
            <div className="px-5 py-3 flex justify-between">
              <span className="text-sm text-gray-500">Días Restantes</span>
              <span className={`text-sm font-bold ${isOverdue ? "text-red-600" : isExpiringSoon ? "text-amber-600" : "text-green-600"}`}>
                {days !== null ? (isOverdue ? `Vencido hace ${Math.abs(days)} días` : `${days} días`) : "—"}
              </span>
            </div>
          </div>
        </div>

        {/* Client Info */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-5 py-3 bg-gray-50 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-700">Información del Cliente</h3>
          </div>
          <div className="divide-y divide-gray-100">
            <div className="px-5 py-3 flex justify-between">
              <span className="text-sm text-gray-500">Cliente</span>
              <span className="text-sm font-medium text-gray-900">{contract.clientName}</span>
            </div>
            <div className="px-5 py-3 flex justify-between">
              <span className="text-sm text-gray-500">Municipio</span>
              <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-indigo-50 text-indigo-700">
                {contract.municipio}
              </span>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-5 py-3 bg-gray-50 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-700">Acciones</h3>
          </div>
          <div className="p-5 space-y-3">
            <a
              href={`https://orgde5cf563.crm9.dynamics.com/main.aspx?appid=&pagetype=entityrecord&etn=cr49f_atcontract&id=${contract.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full text-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-medium"
            >
              Abrir en Arrendamiento App
            </a>
            <Link
              to="/contratos"
              className="block w-full text-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition text-sm font-medium"
            >
              Volver a Contratos
            </Link>
          </div>
        </div>

        {/* Documents */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden md:col-span-2">
          <div className="px-5 py-3 bg-gray-50 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-700">Documentos</h3>
          </div>
          {docsLoading ? (
            <div className="px-5 py-4 text-sm text-gray-400">Cargando documentos...</div>
          ) : documents.length === 0 ? (
            <div className="px-5 py-4 text-sm text-gray-400">No hay documentos disponibles.</div>
          ) : (
            <div className="divide-y divide-gray-100">
              {documents.map((doc) => (
                <a
                  key={doc.webUrl}
                  href={doc.webUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 px-5 py-3 hover:bg-blue-50 transition group"
                >
                  <svg className="w-8 h-8 text-red-400 shrink-0" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 1.5L18.5 9H13V3.5zM12 17H8v-1h4v1zm4-3H8v-1h8v1zm0-3H8v-1h8v1z"/>
                  </svg>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 group-hover:text-blue-700 truncate">{doc.name}</p>
                    <p className="text-xs text-gray-400">
                      {(doc.size / 1024).toFixed(0)} KB · {new Date(doc.createdDate).toLocaleDateString("es-PR")}
                    </p>
                  </div>
                  <svg className="w-4 h-4 text-gray-400 group-hover:text-blue-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const ContractDetailPage: React.FC = () => {
  return (
    <ProtectedRoute>
      <ContractDetailContent />
    </ProtectedRoute>
  );
};
